/**
 * Helper functions for account-based operations
 */

import type { Symbol } from '@/vybe-trader-client/symbols/types';
import { generateAccountSymbolKey } from '../indexdb';
import { useAccountsStore } from '@/lib/accounts/store';

// Get current active account
export const getCurrentAccount = () => {
  try {
    const { activeAccountId, accounts } = useAccountsStore.getState();
    return activeAccountId ? accounts.find((acc: any) => acc.id === activeAccountId) : null;
  } catch (error) {
    console.error('[Chart] Error getting current account:', error);
    return null;
  }
};

// Generate account-specific key for a symbol
export const generateAccountSymbolKeyForCurrent = (symbol: Symbol): string | null => {
  const account = getCurrentAccount();
  if (!account) {
    console.error('[Chart] No active account found');
    return null;
  }
  
  return generateAccountSymbolKey(account.companyName, account.serverName, symbol.currency);
};

// Check if current account is valid
export const isCurrentAccountValid = (): boolean => {
  const account = getCurrentAccount();
  return !!(account && account.companyName && account.serverName);
};

// Get account info for logging
export const getCurrentAccountInfo = (): string => {
  const account = getCurrentAccount();
  return account ? `${account.companyName}_${account.serverName}` : 'no_account';
};

// Generate account key (without symbol)
export const generateAccountKey = (activeAccountId: string | null, accounts: any[]): string | null => {
  if (!activeAccountId) return null;
  
  const account = accounts.find((acc: any) => acc.id === activeAccountId);
  if (!account) return null;
  
  return `${account.companyName}_${account.serverName}`;
};

/** Server trading account key: `{login} {host}:{port} (MT5)` */
export function formatTradingAccountKey(account: {
  credentials: { login: string };
  lastConnectedHost?: string;
  lastConnectedPort?: number;
}): string | null {
  const login = account.credentials?.login?.trim();
  const host = account.lastConnectedHost;
  const port = account.lastConnectedPort;
  if (!login || !host || !port) return null;
  return `${login} ${host}:${port} (MT5)`;
}
